/**
 * Career Vision Service
 *
 * Frontend service for saving and loading career vision data.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

export interface CareerVisionData {
  shortTermGoals: string;
  midTermGoals: string;
  longTermGoals: string;
  values: string[];
  likes: string[];
  dislikes: string[];
  idealRole: string;
  profileSummary?: string;
}

const EMPTY_VISION: CareerVisionData = {
  shortTermGoals: '',
  midTermGoals: '',
  longTermGoals: '',
  values: [],
  likes: [],
  dislikes: [],
  idealRole: '',
};

export async function saveCareerVision(data: CareerVisionData): Promise<void> {
  const functions = getFunctions();
  const fn = httpsCallable(functions, 'saveCareerVision');
  await fn(data);
}

export async function getCareerVision(): Promise<CareerVisionData> {
  const functions = getFunctions();
  const fn = httpsCallable<unknown, { exists: boolean; data: CareerVisionData | null }>(functions, 'getCareerVision');
  const result = await fn();
  if (result.data.exists && result.data.data) {
    return result.data.data;
  }
  return EMPTY_VISION;
}

export type StepKey = 'shortTermGoals' | 'midTermGoals' | 'longTermGoals' | 'values' | 'likes' | 'dislikes' | 'idealRole';

interface ExtractionResult {
  content?: string;
  items?: string[];
}

export async function extractCareerVisionFromTranscript(
  transcript: string,
  section: StepKey,
  language: string
): Promise<ExtractionResult> {
  const functions = getFunctions();
  const fn = httpsCallable<{ transcript: string; section: string; language: string }, ExtractionResult>(
    functions,
    'extractCareerVisionFromTranscript'
  );
  const result = await fn({ transcript, section, language });
  return result.data;
}

export async function generateCareerProfile(
  data: CareerVisionData,
  language: string
): Promise<string> {
  const functions = getFunctions();
  const fn = httpsCallable<CareerVisionData & { language: string }, { profileSummary: string }>(
    functions,
    'generateCareerProfile'
  );
  const result = await fn({ ...data, language });
  return result.data.profileSummary;
}
